import React, { PureComponent } from 'react'
import {
  string, long, func, shape, instanceOf, arrayOf, bool,
} from 'prop-types'
import prop from 'lodash/fp/prop'
import { format as formatDateTime } from '@active/core/datetime'
import { withI18n } from '@active/react-utils/i18n'
import Grid from '@active/react-ui/Grid'
import GridItem from '@active/react-ui/GridItem'
import Link from '@active/react-ui/Link'
import DefaultComponentLayout, { LayoutContent } from '../../../common/Layout'
import CoppaEmailInput from '../../../common/CoppaEmailInput'
import { noop, reduceBlankProp, merge } from '../../../../shared/utils/commonUtils'
import CreateAccountForm, { CreateAccountFormDefaultTypes, CreateAccountFormPropTypes } from './CreateAccountForm'
import MFAV2ByStep from '../../../generalSigninUp/MFAV2/MFAV2ByStep'
import { mfaConfig } from '../../../common/MFAByStep'
import PhoneAuthenticationByStep from '../../../Signin/components/PhoneAuthentication/PhoneAuthenticationByStep'

import './CreateAccountCommon.scss'
import I18nMessage from '../../../common/I18nMessage'
import connectLocale from '../../../../shared/utils/connectLocale'

class CreateAccountDetail extends PureComponent {
  static propTypes = {
    locale: string.isRequired,
    // Config for CreateAccount
    hasSignupTitle: bool,
    hasSignInLink: bool,
    fields: shape({
      email: string,
      firstName: string,
      lastName: string,
      dob: instanceOf(Date),
      countryCode: string,
      signedWaiverIds: arrayOf(long),
    }),

    Header: func,
    processes: shape({
      signIn: func,
      signUp: func,
    }),
    clientConfig: shape({
      disableSignInLink: bool,
      suppressActiveWaiver: bool,
      useCustomizeWaiver: bool,
      waiverAttachementObjectIds: string,
      keycloakModeEnable: bool,
    }).isRequired,

    // Config for COPPA
    configureTrack: func.isRequired,
    services: shape({
      verification: func.isRequired,
      createAccount: func.isRequired,
    }).isRequired,

    // Config for CreateAccountForm
    enableCoppaFlow: bool,
    hasDefaultCreateHandle: bool,
    amEighteen: bool,
    epid: string,
    sace: bool,
    i18n: func.isRequired,
    onCreateAccountSuccess: func,
    actions: shape({
      existAccountCallback: func,
      signupCountDownCallback: func,
    }),

    triggers: shape({
      createAccountTrigger: func,
    }),

    ...CreateAccountFormPropTypes,

    countryCodes: string,
    isPage: bool,

    additionalData: shape({
      primaryAddress: shape({
        line1: string,
        line2: string,
        city: string,
        stateProvince: string,
        country: string,
        postalCode: string,
        addressType: string,
      }),
      primaryPhoneNumber: shape({
        extension: string,
        phoneNumber: string,
        phoneNumberType: string,
      }),
    }),
    createAccountOptional: bool,

    authWithKeycloak: bool,
    onKeycloakSubmit: func,
  }

  static defaultProps = {
    hasSignupTitle: false,
    hasSignInLink: false,
    triggers: {
      createAccountTrigger: noop,
    },
    fields: {},
    processes: {
      signIn: null,
      signUp: null,
    },
    Header: null,
    // Config for CreateAccountForm
    ...CreateAccountFormDefaultTypes,
    enableCoppaFlow: false,
    hasDefaultCreateHandle: false,
    epid: null,
    sace: false,
    amEighteen: false,
    actions: {
      existAccountCallback: noop,
      signupCountDownCallback: noop,
    },
    countryCodes: undefined,
    isPage: false,
    onCreateAccountSuccess: undefined,

    additionalData: undefined,
    createAccountOptional: false,

    authWithKeycloak: false,
    onKeycloakSubmit: null,
  }

  constructor(props) {
    super(props)
    this.state = {
      errMsg: null,

      showCoppaEmailInput: false,
      pendingKey: null,

      handleSubmitResultCallback: noop,
      submitFormTrigger: noop,
      setFormFieldError: noop,

      nextState: {},
    }
  }

  componentDidMount() {
    const { configureTrack, triggers: { createAccountTrigger } } = this.props
    configureTrack({
      eventName: 'CreateAccountDetail',
      widget: 'create_account_detail',
      pageType: 'create account detail',
    })
    if (createAccountTrigger) {
      createAccountTrigger(this.handleCreateAccount)
    }
  }

  handleCreateAccount = (callback, params, partialUpdate) => {
    const { submitFormTrigger } = this.state
    this.setState({
      handleSubmitResultCallback: callback,
    }, () => {
      submitFormTrigger(params, partialUpdate)
    })
  }

  submitFormTriggerBuilder = (formTrigger, setFormFieldError) => {
    this.setState({
      submitFormTrigger: formTrigger,
      setFormFieldError: setFormFieldError,
    })
  }

  handleCOPPAShow = pendingKey => {
    this.setState({
      showCoppaEmailInput: true,
      pendingKey: pendingKey,
    })
  }

  handleErrorMessage = errMsg => {
    this.setState({ errMsg })
  }

  handleExistAccount = email => {
    const { actions: { existAccountCallback }, processes: { signIn, signUp: goBack }, hasSignInLink } = this.props
    if (hasSignInLink && signIn) {
      signIn({
        goBack,
        username: email,
        existingAccount: true,
        goSignUp: '',
      })
    }
    if (existAccountCallback) {
      existAccountCallback({ existAccount: email })
    }
  }

  handleSignIn = () => {
    const { processes: { signIn } } = this.props
    if (signIn) {
      signIn({ goSignUp: '' })
    }
  }

  handleCallbackFailed = (message, response, ignoreHandleErrorMessage) => {
    if (!ignoreHandleErrorMessage) {
      this.handleErrorMessage(message)
    }
    const { handleSubmitResultCallback } = this.state
    // Do not need to consider EMPTY here due to empty password will be reject by front validation
    handleSubmitResultCallback({ ...response, status: 'ERROR', message })
  }

  handleKeycloakCheckAccount = ({params, resolve}) => {
    const { onKeycloakSubmit } = this.props
    const data = {
      email: params.email,
      detailAction: 'CHECK_USERNAME_ACTION'
    }
    onKeycloakSubmit(data).then(result => resolve(result))
  }

  handleKeycloakCreateAccount = ({params, resolve}) => {
    const { onKeycloakSubmit } = this.props
    const data = {
      email: params.email,
      firstName: params.firstName,
      lastName: params.lastName,
      password: params.password,
      dob: params.originDob,
      countryCode: params.countryCode,
      confirmPassword: params.confirmPassword,
      agreed: params.agreed,
      detailAction: 'DETAIL_ACTION'
    }
    onKeycloakSubmit(data).then(result => {
      resolve(result)
    })
  }

  submitRegistrationPostActions = params => {
    const { onKeycloakSubmit } = this.props
    onKeycloakSubmit(params).then(result => {
      this.onRegistrationPostActionsResponse(result)
    })
  }

  onRegistrationPostActionsResponse = ({ status, username, ...rest }) => {
    let nextState = this.state.nextState
    if (status === 'MFA_INITED') {
      nextState = merge(nextState, {
        isShowMFAV2: true,
        mfaMessageType: prop('messageType')(rest),
        mfaMessageContent: prop('messageContent')(rest),
        newUserName: username,
        senderPhone: prop('senderPhone')(rest),
        isTrustDevice: prop('isTrustDevice')(rest),
        nextTimeWindow: prop('nextTimeWindow')(rest),
        stepMode: prop('stepMode')(rest),
        defaultWayForMfaStepOne: prop('defaultWay')(rest),
        channel: prop('channel')(rest),
        isShowBackBtnForMfaStepTwo: prop('isShowBackBtn')(rest),
        locale: prop('locale')(rest),
      })
    } else if (status === 'PHONE_SETUP_INITED') {
      nextState = merge(nextState, {
        isPhoneAuthenticationShow: true,
        epid: prop('epid')(rest),
        stepMode: prop('stepMode')(rest),
        senderPhone: prop('senderPhone')(rest),
        nextTimeWindow: prop('nextTimeWindow')(rest),
      })
    }
    this.setState({nextState: nextState}) 
  }

  handleMFAV2Operation = params => {
    const { onKeycloakSubmit } = this.props
    const { mfaMessageContent } = this.state.nextState
    
    if (mfaMessageContent) {
      // Ensure error message show when content didn't changed after action.
      this.setState({ mfaMessageContent: '' })
    }
    let nextState = this.state.nextState
    if (params.operation === mfaConfig.Operations.CLOSE) {
      const timer = setTimeout(() => {
        clearTimeout(timer)
        nextState = merge(nextState, {
          isShowMFAV2: false,
        })
        this.setState({nextState: nextState})
      }, 300)
      this.submitRegistrationPostActions(params)
    } else if (params.operation === mfaConfig.Operations.RESENDCODE) {
      onKeycloakSubmit(params).then(({ nextTimeWindow, messageContent, messageType }) => {
        nextState = merge(nextState, {
          nextTimeWindow,
          mfaMessageContent: messageContent,
          mfaMessageType: messageType,
        })
        this.setState({nextState: nextState})
      })
    } else {
      this.submitRegistrationPostActions(params)
    }
  }

  handleSetUpPhone = values => {
    const {
      onKeycloakSubmit,
      clientConfig: {
        suppressActiveWaiver,
        useCustomizeWaiver,
        waiverAttachementObjectIds,
      },
      countryCodes,
    } = this.props

    return new Promise(resolve => {
      onKeycloakSubmit({
        ...values,
        suppressActiveWaiver,
        useCustomizeWaiver,
        waiverAttachementObjectIds,
        countriesInfo: countryCodes,
        needReCOPPA: true,
        needAccountCountry: true,
      }).then(result => {
        const {
          status, message, errorCode
        } = result
        if (status === 'FAILED') {
          resolve({ message, errorCode })
        } else {
          this.onRegistrationPostActionsResponse(result)
        }
      })
    })
  }

  handleSubmit = values => {
    const {
      // config
      locale, amEighteen, enableCoppaFlow, hasDefaultCreateHandle, onCreateAccountSuccess, actions: {
        signupCountDownCallback, keycloakSigninCallback, signinSuccessCallback,
      },
      // data
      epid, sace, countryCodes, clientConfig: {
        suppressActiveWaiver, useCustomizeWaiver, waiverAttachementObjectIds, keycloakModeEnable,
      },
      additionalData,
      // lib
      i18n, services, isPage,
      createAccountOptional,
      authWithKeycloak,
    } = this.props

    if (createAccountOptional) {
      // When createAccountOptional is true and one of the create account required field value is not exsist,
      // we should not call createAccount api
      const {
        email, firstName, lastName, password,
      } = values
      const { handleSubmitResultCallback } = this.state
      if (!email || !firstName || !lastName || !password) {
        handleSubmitResultCallback({ status: 'NOT_CREATE_ACCOUNT' })
        return
      }
    }

    let createAccount = authWithKeycloak ? this.handleKeycloakCreateAccount : services.createAccount
    const data = {
      ...values,
      countriesInfo: countryCodes,
      firstname: values.firstName,
      lastname: values.lastName,
      regagree: true,
      suppressActiveWaiver,
      useCustomizeWaiver,
      waiverAttachementObjectIds,
      amEighteen,
      keycloakModeEnable,
    }
    if (epid) { data.epid = epid }
    if (additionalData) { data.additionalData = JSON.stringify(additionalData) }
    if (!isPage) { data.isWidget = true }
    // Suppress account creation email if 'sace' is true or 'true'
    if (sace === true || sace === 'true') { data.suppressAccountCreationEmail = true }
    const params = reduceBlankProp(data)
    if (values.dob) {
      params.originDob = values.dob
      params.dob = formatDateTime('MM/dd/yyyy')(values.dob)
    }

    createAccount({
      params,
      resolve: ({
        status, loginURL, ssoToken, message, pendingKey, reason, ...rest
      }) => {
        const response = {
          email: values.email, sace, status, loginURL, ssoToken, message, pendingKey, reason, ...rest,
        }

        if (status === 'SUCCESS') {
          if (hasDefaultCreateHandle) {
            const IS_ESCAPE_CRA_CONF_WIDGET = false // todo

            const defaultHandleSignupSuccess = signupCountDownCallback ? () => {
              response.locale = locale
              signupCountDownCallback(response)
            } : () => {
              if (keycloakModeEnable && (!!keycloakSigninCallback || !!signinSuccessCallback)) {
                const url = new URL(window.location.href)
                url.searchParams.delete('goSignUp')
                window.location = url.href
              } else {
                const url = new URL(loginURL)
                url.searchParams.append('ssoToken', ssoToken)
                url.searchParams.append('locale', locale)
                window.location = url.href
              }
            }

            if (IS_ESCAPE_CRA_CONF_WIDGET) { // todo
              defaultHandleSignupSuccess()
            } else if (onCreateAccountSuccess) {
              onCreateAccountSuccess({ defaultHandleSignupSuccess, ...response })
            } else {
              defaultHandleSignupSuccess()
            }
          }
          const { handleSubmitResultCallback } = this.state
          handleSubmitResultCallback(response)
        } else if (status === 'CREATE_USER_DONE') { // widget v5 only
            const defaultHandleSignupSuccess = () => {
              if (keycloakModeEnable && (!!keycloakSigninCallback || !!signinSuccessCallback)) {
                const url = new URL(window.location.href)
                url.searchParams.delete('goSignUp')
                window.location = url.href
              } else {
                const url = new URL(loginURL)
                url.searchParams.append('ssoToken', ssoToken)
                url.searchParams.append('locale', locale)
                window.location = url.href
              }
            }
            const signupSuccesscallbackData = {
              ...response,
              status: 'SUCCESS',
              username: values.email,
              loginURL: window.APWWidget.keycloakUrl + (loginURL.startsWith('/v5') ? loginURL.substring(3) : loginURL)
            }
            onCreateAccountSuccess({defaultHandleSignupSuccess, ...signupSuccesscallbackData})
            // submit w_create_user_done.ftl
            this.submitRegistrationPostActions() 
        } else if (status === 'FAILURE' && message) {
          let msg = i18n('passport.error.server.default')
          let ignoreHandleErrorMessage = false
          if (message === 'COPPA') {
            if (enableCoppaFlow) {
              this.handleCOPPAShow(pendingKey)
              msg = message
            } else {
              msg = i18n('passport.error.server.ineligible.age')
            }
          } else if (message === 'INELIGIBLE_FOR_REGISTRATION') {
            if (reason === 'AGE_REQUIREMENT') {
              msg = i18n('passport.error.server.ineligible.age')
            } else {
              msg = i18n('passport.error.server.ineligible')
            }
          } else if (message === 'USERNAME_EXISTS' || message === 'USER_NAME_DUPLICATE') {
            msg = i18n('passport.error.server.existAccount')
          } else if (isPage && message === 'CAPTCHA_FAIL') {
            msg = i18n('passport.error.server.recaptcha.fail')
          } else if (message === 'PSW_NOT_QUALFIED') {
            msg = i18n('passport.common.notice.pwdDescription')
            const { setFormFieldError } = this.state
            setFormFieldError('password', msg)
            ignoreHandleErrorMessage = true
          }
          this.handleCallbackFailed(msg, response, ignoreHandleErrorMessage)
        } else {
          this.handleCallbackFailed('', response)
        }
      },
      reject: ({ message }, ...rest) => {
        this.handleCallbackFailed(message || '', rest)
      },
    })
  }

  render() {
    const { Header, authWithKeycloak, onKeycloakSubmit, configureTrack } = this.props
    const { showCoppaEmailInput } = this.state
    const { 
      isShowMFAV2, mfaMessageType, mfaMessageContent, newUserName, isTrustDevice, nextTimeWindow, 
      stepMode, defaultWayForMfaStepOne, channel, isShowBackBtnForMfaStepTwo, locale,
      isPhoneAuthenticationShow, epid, senderPhone, 
    } = this.state.nextState

    if (showCoppaEmailInput) {
      const { configureTrack, services } = this.props
      const { pendingKey } = this.state
      const coppaEmailProps = {
        pendingKey, Header, services, configureTrack,
      }
      return (
        <CoppaEmailInput {...coppaEmailProps} authWithKeycloak={authWithKeycloak} onKeycloakSubmit={onKeycloakSubmit} />
      )
    }

    const {
      hasSignInLink, hasSignupTitle, fields, clientConfig, clientConfig: { disableSignInLink } = {},
      // CreateAccountForm
      emailField, firstNameField, lastNameField, dobField, countryField, passwordField,
      ...rest
    } = this.props
    const { errMsg, handleSubmitResultCallback } = this.state
    const {
      email, firstName, lastName, dob, countryCode,
    } = fields
    return (
      <DefaultComponentLayout nameSpace="passport__create-account" Header={Header} errMsg={errMsg}>
        {hasSignupTitle && <LayoutContent title message="passport.signup.title" />}
        <LayoutContent main>
          <CreateAccountForm
            handleErrorMessage={this.handleErrorMessage}
            handleExistAccount={this.handleExistAccount}
            handleCreationSubmit={this.handleSubmit}
            handldeValidationFailedCallback={handleSubmitResultCallback}
            submitFormTriggerBuilder={this.submitFormTriggerBuilder}
            handleCheckAccount={authWithKeycloak ? this.handleKeycloakCheckAccount : undefined}
            emailField={{
              value: email, changeAble: false, editAble: true, ...emailField,
            }}
            firstNameField={{ value: firstName, ...firstNameField }}
            lastNameField={{ value: lastName, ...lastNameField }}
            dobField={{ value: dob, ...dobField }}
            countryField={{ value: countryCode, ...countryField }}
            passwordField={passwordField}
            clientConfig={clientConfig}
            {...rest}
          />
          {hasSignInLink && !disableSignInLink && (
            <Grid colGap={0} rowGap={10}>
              <GridItem>
                <div className="passport__form__subtitle">
                  <I18nMessage id="passport.signup.notice.haveAccount" />
                  <Link
                    tabIndex={0}
                    onClick={this.handleSignIn}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        this.handleSignIn()
                      }
                    }}
                    className="passport__pl5"
                  >
                    <I18nMessage id="passport.common.btn.signIn" />
                  </Link>
                </div>
              </GridItem>
            </Grid>
          )}
          {isShowMFAV2 && (
            <MFAV2ByStep
              messageType={mfaMessageType || 'error'}
              messageContent={mfaMessageContent}
              senderEmail={newUserName}
              senderPhone={senderPhone}
              isTrustDevice={isTrustDevice}
              nextTimeWindow={nextTimeWindow}
              locale={locale}
              handleOperation={this.handleMFAV2Operation}
              stepMode={stepMode}
              defaultWay={defaultWayForMfaStepOne}
              channel={channel}
              isShowBackBtn={isShowBackBtnForMfaStepTwo}
            />
          )}
          {isPhoneAuthenticationShow && (
            <PhoneAuthenticationByStep
              onSetUpPhone={this.handleSetUpPhone}
              configureTrack={configureTrack}
              epid={epid}
              locale={locale}
              senderPhone={senderPhone}
              nextTimeWindow={nextTimeWindow}
              stepMode={stepMode}
            />
          )}
        </LayoutContent>
      </DefaultComponentLayout>
    )
  }
}

export default withI18n(connectLocale(CreateAccountDetail))
